import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState({});
  const [services, setServices] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [dropdownHoverTimeout, setDropdownHoverTimeout] = useState(null);
  const location = useLocation();

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    fetchSettings();
    fetchServices();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu when route changes
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setServicesDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (dropdownHoverTimeout) clearTimeout(dropdownHoverTimeout);
    };
  }, [dropdownHoverTimeout]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (servicesDropdownOpen && !event.target.closest('.relative')) {
        setServicesDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [servicesDropdownOpen]);

  // Detect if current page has dark background (hero section, etc.)
  // For service detail pages, use dark text initially for better contrast during load
  const isServiceDetailPage = location.pathname.startsWith('/services/');
  const isDarkBackground = !scrolled && location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#0A1A50] shadow-lg backdrop-blur-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-24">
          <Link 
            to="/" 
            className={`flex items-center transition-all duration-300 hover:scale-105`}
          >
            {settings.site_logo ? (
              <img 
                src={settings.site_logo} 
                alt={settings.site_name || 'Logo'} 
                className={`w-auto transition-all duration-300 ${
                  scrolled ? 'h-14' : 'h-20 filter drop-shadow-lg'
                }`}
                style={{
                  filter: scrolled ? 'brightness(0) invert(1)' : 'none'
                }}
              />
            ) : (
              <span className={`font-light tracking-wide transition-all duration-300 ${
                scrolled ? 'text-2xl' : 'text-3xl'
              } ${
                scrolled ? 'text-white' : (isDarkBackground ? 'text-white drop-shadow-lg' : 'text-[#0A1A50]')
              }`}>
                {settings.site_name || 'Reagle.'}
              </span>
            )}
          </Link>
          
          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => {
                if (dropdownHoverTimeout) clearTimeout(dropdownHoverTimeout);
                setServicesDropdownOpen(true);
              }}
              onMouseLeave={() => {
                const timeout = setTimeout(() => {
                  setServicesDropdownOpen(false);
                }, 300);
                setDropdownHoverTimeout(timeout);
              }}
            >
              <button
                onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                className={`font-light transition-all duration-300 hover:scale-105 flex items-center gap-1 ${
                  location.pathname.startsWith('/services/') ? 'opacity-70' : ''
                } ${
                  scrolled 
                    ? 'text-white hover:text-white/80' 
                    : (isDarkBackground 
                        ? 'text-white hover:text-white/80' 
                        : (isServiceDetailPage 
                            ? 'text-[#2d2d2d] hover:text-[#0A1A50]' 
                            : 'text-[#2d2d2d] hover:text-[#0A1A50]'))
                }`}
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {servicesDropdownOpen && services.length > 0 && (
                <div 
                  className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-2xl py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                  onMouseEnter={() => {
                    if (dropdownHoverTimeout) clearTimeout(dropdownHoverTimeout);
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => {
                      setServicesDropdownOpen(false);
                    }, 300);
                    setDropdownHoverTimeout(timeout);
                  }}
                >
                  {services.map((service) => (
                    <Link
                      key={service.service_id}
                      to={`/services/${service.service_id}`}
                      className="block px-6 py-3 text-[#2d2d2d] hover:bg-gray-50 hover:text-[#0A1A50] transition-colors font-light"
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`font-light transition-all duration-300 hover:scale-105 ${
                location.pathname === '/about' ? 'opacity-70' : ''
              } ${
                scrolled 
                  ? 'text-white hover:text-white/80' 
                  : (isDarkBackground 
                      ? 'text-white hover:text-white/80' 
                      : (isServiceDetailPage 
                          ? 'text-[#2d2d2d] hover:text-[#0A1A50]' 
                          : 'text-[#2d2d2d] hover:text-[#0A1A50]'))
              }`}
            >
              About Us
            </Link>
            <Link to="/contact">
              <Button 
                className={`rounded-full px-6 transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  scrolled ? 'bg-white text-[#0A1A50] hover:bg-white/90' : 'bg-[#0A1A50] text-white hover:bg-[#060F2E]'
                }`}
              >
                Connect
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className={`w-6 h-6 ${scrolled || isDarkBackground ? 'text-white' : 'text-[#2d2d2d]'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${scrolled || isDarkBackground ? 'text-white' : 'text-[#2d2d2d]'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-6 py-4 space-y-4">
            {/* Mobile Services Dropdown */}
            <div>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className={`flex items-center justify-between w-full py-2 font-light text-[#2d2d2d] hover:text-[#0A1A50] transition-colors ${
                  location.pathname.startsWith('/services/') ? 'text-[#0A1A50] font-normal' : ''
                }`}
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 mt-2 space-y-2">
                  {services.map((service) => (
                    <Link
                      key={service.service_id}
                      to={`/services/${service.service_id}`}
                      className="block py-2 text-sm font-light text-[#2d2d2d] hover:text-[#0A1A50] transition-colors"
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link
              to="/about"
              className={`block py-2 font-light text-[#2d2d2d] hover:text-[#0A1A50] transition-colors ${
                location.pathname === '/about' ? 'text-[#0A1A50] font-normal' : ''
              }`}
            >
              About Us
            </Link>
            <Link to="/contact" className="block">
              <Button className="w-full bg-[#0A1A50] text-white hover:bg-[#060F2E] rounded-full">
                Connect
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;